// React
import { useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback } from '../ui/avatar';

// Reducer
import { store } from '../../ReduxRoot';
import { useSelector } from 'react-redux';

// FutureLab
import { ChatListDataInterface } from './ChatListItemsMobile';
import { chatItemInFocus } from '../../actions/chatItemInFocusActions';
import { RootState } from '../../reducers/index';
import { UserDetailsListItem } from '../../model';
import { isSameUserId } from '../../utilities/UserId';

// 3rd Party
import moment from 'moment';

interface ChatListChatRequestComponentInterface {
	pendingChatListsItem: ChatListDataInterface;
	userListProfilesDetails: UserDetailsListItem[];
	navigateOnSelect?: boolean;
}

export function ChatListChatRequest(chatListChatRequest: ChatListChatRequestComponentInterface) {
	const navigate = useNavigate();
	const chatInFocus = useSelector((state: RootState) => state.chatInFocus);
	const { pendingChatListsItem, userListProfilesDetails, navigateOnSelect = false } = chatListChatRequest;

	const getGroupChatNameFromParticipantNameInitials = (): string => {
		const groupParticipantIds = pendingChatListsItem.participants.map((participant) => participant.userId);
		const userListProfilesFullDetails = userListProfilesDetails.filter((userListProfileDetails) =>
			groupParticipantIds.some((userId) => isSameUserId(userId, userListProfileDetails.id))
		);
		return userListProfilesFullDetails[0]?.firstname?.charAt(0) || '?';
	};

	const getGroupChatNameInitials = (): string => {
		const nameFromDb = pendingChatListsItem?.name?.trim()?.charAt(0);
		return nameFromDb ? nameFromDb : getGroupChatNameFromParticipantNameInitials();
	};

	const getGroupChatNameFromParticipantNames = (): string => {
		const groupParticipantIds = pendingChatListsItem.participants.map((participant) => participant.userId);
		const userListProfilesFullDetails = userListProfilesDetails.filter((userListProfileDetails) =>
			groupParticipantIds.some((userId) => isSameUserId(userId, userListProfileDetails.id))
		);
		return userListProfilesFullDetails
			.map((user) => user.firstname?.trim())
			.filter(Boolean)
			.join(', ') || 'Unnamed group';
	};

	const getGroupChatName = (): string => {
		const nameFromDb = pendingChatListsItem.name?.trim();
		return nameFromDb ? nameFromDb : getGroupChatNameFromParticipantNames();
	};

	const selectPendingApprovalChat = (pendingChatListItem: ChatListDataInterface) => {
		if (navigateOnSelect) {
			navigate('../chat/chat-room-pending');
		}
		store.dispatch(chatItemInFocus(pendingChatListItem));
	};

	return (
		<div className="border-b border-slate-200 bg-white">
			<button
				type="button"
				aria-label={`Open invitation to ${getGroupChatName()}`}
				className={
					chatInFocus?.id === pendingChatListsItem?.id
						? 'w-full bg-primary/10 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'
						: 'w-full px-4 py-3 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'
				}
				onClick={() => selectPendingApprovalChat(pendingChatListsItem)}
			>
				<div className="grid grid-cols-[3.25rem_minmax(0,1fr)] gap-3">
					<div className="shrink-0 p-0">
						<Avatar className="chat-list-image-holder rounded-md">
							<AvatarFallback className="rounded-md">
								{getGroupChatNameInitials().toLocaleUpperCase()}
							</AvatarFallback>
						</Avatar>
					</div>
					<div className="min-w-0 self-center">
						<div className="flex min-w-0 items-baseline gap-2">
							<div className="min-w-0 flex-1 truncate pb-1 text-sm font-semibold capitalize text-slate-900">
								{getGroupChatName()}
							</div>
							<div className="shrink-0 text-right text-xs text-slate-500">
								{moment(pendingChatListsItem.createdAt).format(
									moment(pendingChatListsItem.createdAt).isSame(moment(), 'day')
										? 'h:mma'
										: 'h:mma, D/M'
								)}
							</div>
						</div>
						<div className="truncate text-sm text-slate-500">
							You have a new group invitation
						</div>
					</div>
				</div>
			</button>
		</div>
	);
}
